import { useState } from "react";
import { useApi } from "../api/useApi.js";
import {
  Basis,
  ConfidencePill,
  Drawer,
  EmptyState,
  HonestState,
  RagDot,
  type BasisKind,
  type Rag,
} from "../components.js";
import { DerivedMetric } from "./ProjectAnalyticsPanels.js";
import { EvidenceContent } from "./EvidenceContent.js";
import type {
  AccuracyValue,
  BiasValue,
  CommitmentTrendValue,
  DivergenceValue,
  EstimateDerivedDateValue,
  EstimateFreeForecastDateValue,
  EstimateTrackRecordValue,
  EstimationCapabilityValue,
  EstimationSummaryValue,
  LaterSide,
  Metric,
  PeriodReliabilityValue,
  SpreadValue,
} from "./projectMetrics.js";

/**
 * NX-2634 (E13.8) — the Estimation panel on Project detail (design doc 04a §8), alongside the
 * existing Capability/Friction/Reliability panels (ProjectAnalyticsPanels.tsx).
 *
 * WHY THIS IS ITS OWN FILE, not a fourth panel in ProjectAnalyticsPanels.tsx: that module's own
 * docstring states its panels "never fetch — every panel here is presentational: it takes its slice
 * of the ALREADY-fetched envelope as props" (the capability/friction/reliability/DORA fields all ride
 * on `GET /projects/:id`'s single envelope). Estimation data lives behind a SEPARATE endpoint,
 * `GET /projects/:id/estimation` (apps/api/src/estimation-api.ts, E13.7/NX-2630) — AC5 requires this
 * panel to fetch it itself, through `useApi`, exactly like `EvidenceContent`/`Evidence.tsx` do for
 * their own independent resource. Adding a fetching component to a module whose entire stated
 * contract is "never fetches" would falsify that module's own doc comment for every existing reader,
 * so this stays a sibling, single-responsibility file — composed into Project.tsx by one JSX line,
 * never written inline there (AC1).
 *
 * ROLE PROJECTION (AC6 — Papa's own review, not self-verified here): the API's `ESTIMATION_POLICY`
 * (estimation-api.ts) is default-deny and DELETES denied keys via `projectFields` — a client-viewer's
 * envelope literally lacks `accuracy`/`bias`/`spread`/`commitmentReliability`/`divergenceDetail` (they
 * are `undefined`, not `null`, not empty objects). Every section below is gated on `!== undefined`
 * and returns `null` (no JSX at all) when absent, so a role-projected-away section is a genuine DOM
 * absence — never a CSS-hidden node. See this story's report for the exact plain-English client-viewer
 * subset.
 */

// --- the role-projected response shape (mirrors estimation-api.ts's EstimationView) -------------

export interface EstimationView {
  readonly projectId: string;
  readonly effectiveAt: string;
  readonly coverage: Metric<EstimationCapabilityValue>;
  readonly summary: EstimationSummaryValue;
  // Delivery-lead/admin only (ESTIMATION_POLICY.accuracy/bias/spread = DELIVERY_ROLES).
  readonly accuracy?: Metric<AccuracyValue>;
  readonly bias?: Metric<BiasValue>;
  readonly spread?: Metric<SpreadValue>;
  // Delivery-lead/admin/sponsor (DELIVERY_AND_SPONSOR_ROLES).
  readonly commitmentReliability?: {
    readonly periods: readonly Metric<PeriodReliabilityValue>[];
    readonly trend: Metric<CommitmentTrendValue>;
  };
  readonly divergenceHeadline?: Metric<{ readonly text: string }>;
  // Delivery-lead/admin only.
  readonly divergenceDetail?: {
    readonly estimateDerived: Metric<EstimateDerivedDateValue>;
    readonly estimateFreeForecast: Metric<EstimateFreeForecastDateValue>;
    readonly divergence: Metric<DivergenceValue>;
    readonly trackRecord: Metric<EstimateTrackRecordValue>;
  };
}

// --- local helpers (mirrors the identical per-panel-file convention already used by Project.tsx and
// ProjectAnalyticsPanels.tsx — neither imports a shared util for these) -------------------------

const decimal = (value: number, places = 1): string => String(Number(value.toFixed(places)));
const shortDate = (iso: string): string => (iso.length >= 10 ? iso.slice(0, 10) : iso);
const metricConfidence = <T,>(metric: Metric<T>): number | null =>
  metric.kind === "value" && metric.confidence.kind === "value"
    ? metric.confidence.confidence
    : null;
const metricReason = <T,>(metric: Metric<T>, fallback: string): string =>
  metric.kind === "n/a" ? metric.reason : fallback;

const LATER_SIDE_GAP_LABEL: Record<LaterSide, string> = {
  estimate: "estimate-derived date is later",
  forecast: "evidence-based forecast is later",
  same: "dates align",
};
const LATER_SIDE_GLYPH: Record<LaterSide, string> = { estimate: "▲", forecast: "▼", same: "—" };
/** Client-viewer/readonly get only `summary.divergenceLaterSide` (AC6) — this restates that same
 *  enum in prose, the same direction-only vocabulary `LATER_SIDE_PHRASE` uses server-side
 *  (estimation-api.ts), never a second interpretation of a number the role cannot see. */
const CLIENT_LATER_SIDE_TEXT: Record<LaterSide, string> = {
  estimate: "the estimate-derived date runs later than the evidence-based forecast",
  forecast: "the evidence-based forecast runs later than the estimate-derived date",
  same: "the estimate-derived date and the evidence-based forecast agree",
};

function clickable(label: string, onClick: () => void): React.HTMLAttributes<HTMLDivElement> {
  return {
    role: "button",
    tabIndex: 0,
    "aria-label": `Open ${label} evidence drawer`,
    onClick,
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    },
    style: { cursor: "pointer" },
  } as React.HTMLAttributes<HTMLDivElement>;
}

// --- AC1: trend (accuracy/bias/spread), each a click-to-evidence DerivedMetric ------------------

function TrendSection({
  accuracy,
  bias,
  spread,
  onOpen,
}: {
  accuracy?: Metric<AccuracyValue>;
  bias?: Metric<BiasValue>;
  spread?: Metric<SpreadValue>;
  onOpen: (label: string) => () => void;
}): React.ReactNode {
  // AC6: role-projected away — genuinely absent, not a section rendered empty/hidden.
  if (accuracy === undefined || bias === undefined) return null;
  return (
    <div className="panel pad enter">
      <h3 className="sec">Accuracy &amp; bias trend</h3>
      <div className="grid cols-2">
        <DerivedMetric
          label="Estimate accuracy"
          metric={accuracy}
          onClick={onOpen("Estimate accuracy")}
          signal={(v) => v.meanRatio}
          render={(v) => (
            <>
              {decimal(v.meanRatio, 2)}× <small>mean ratio over {v.n} items</small>
            </>
          )}
        />
        <DerivedMetric
          label="Estimate bias"
          metric={bias}
          onClick={onOpen("Estimate bias")}
          render={(v) =>
            v.direction === "balanced" ? (
              "Balanced"
            ) : (
              <>
                {decimal(v.magnitude * 100)}% <small>{v.direction}-estimated</small>
              </>
            )
          }
        />
        {spread !== undefined && (
          <DerivedMetric
            label="Estimate spread"
            metric={spread}
            onClick={onOpen("Estimate spread")}
            render={(v) => (
              <>
                {decimal(v.coefficientOfVariation * 100)}% <small>coefficient of variation</small>
              </>
            )}
          />
        )}
      </div>
    </div>
  );
}

// --- AC1: commitment-reliability gauge (.track — the say/do ratio as a filled bar, never a fabricated
// RAG judgement: the fill IS the observed ratio, no invented threshold) -------------------------

function periodRag(v: PeriodReliabilityValue): Rag {
  if (v.ratio === null) return "neutral"; // open period / nothing committed — a fact, not a judgement
  return v.ratio === 1 ? "green" : "amber"; // exact equality only — never a chosen numeric threshold
}
function periodLabel(v: PeriodReliabilityValue): string {
  if (v.nothingCommitted) return "Nothing committed";
  if (v.ratio === null) return "Period still open";
  return v.ratio === 1 ? "Fully delivered" : "Partially delivered";
}

function CommitmentGauge({
  commitmentReliability,
  onOpen,
}: {
  commitmentReliability?: {
    readonly periods: readonly Metric<PeriodReliabilityValue>[];
    readonly trend: Metric<CommitmentTrendValue>;
  };
  onOpen: (label: string) => () => void;
}): React.ReactNode {
  if (commitmentReliability === undefined) return null; // AC6: role-projected away
  const { trend, periods } = commitmentReliability;
  return (
    <div className="panel pad enter">
      <h3 className="sec">Commitment reliability</h3>
      {trend.kind === "value" ? (
        <div {...clickable("Commitment reliability", onOpen("Commitment reliability"))}>
          <div
            className="track"
            role="meter"
            aria-valuenow={trend.value.ratio === null ? 0 : Math.round(trend.value.ratio * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Say/do ratio"
          >
            <i
              style={{
                width: `${trend.value.ratio === null ? 0 : Math.round(trend.value.ratio * 100)}%`,
              }}
            />
          </div>
          <div className="meta">
            {trend.value.ratio === null ? (
              "no ratio-bearing closed period yet"
            ) : (
              <>
                {decimal(trend.value.ratio * 100)}%{" "}
                <small>say/do over {trend.value.closedPeriods} closed periods</small>
              </>
            )}
            {trend.value.thinHistory && <span className="tag amber"> thin history</span>}
          </div>
          <ConfidencePill value={metricConfidence(trend)} />
        </div>
      ) : (
        <HonestState
          kind="nodata"
          title="Commitment reliability"
          detail={metricReason(trend, "N/A")}
        />
      )}
      {periods.length > 0 && (
        <div className="grid cols-3" style={{ marginTop: 12 }}>
          {periods.map((p, index) =>
            p.kind === "value" ? (
              <div
                key={p.value.periodId}
                className="meta"
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <RagDot status={periodRag(p.value)} label={periodLabel(p.value)} />
                {p.value.periodId}: {p.value.completedCount}/{p.value.committedCount}
              </div>
            ) : (
              // An n/a period is unusual (facts loaded per closed period should resolve), but stays
              // honestly absent-of-number rather than a fabricated row if it ever occurs.
              <div key={`na-${index}`} className="meta">
                {metricReason(p, "N/A")}
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

// --- AC2: estimate-vs-forecast comparison — front and center, three role-shaped renderings --------

function DateCard<T>({
  label,
  basisKind,
  metric,
  onOpen,
  render,
}: {
  label: string;
  basisKind: BasisKind;
  metric: Metric<T>;
  onOpen: () => void;
  render: (value: T) => React.ReactNode;
}): React.ReactNode {
  return (
    <div className="panel pad">
      <Basis kind={basisKind} />
      {metric.kind === "value" ? (
        <div {...clickable(label, onOpen)} style={{ cursor: "pointer", marginTop: 10 }}>
          <div className="lab">{label}</div>
          <div className="v num" style={{ fontSize: 25, fontWeight: 710, marginTop: 4 }}>
            {render(metric.value)}
          </div>
          <div className="mc">
            <ConfidencePill value={metricConfidence(metric)} />
          </div>
        </div>
      ) : (
        <HonestState kind="nodata" title={label} detail={metricReason(metric, "N/A")} />
      )}
    </div>
  );
}

function FullComparison({
  detail,
  headline,
  onOpen,
}: {
  detail: NonNullable<EstimationView["divergenceDetail"]>;
  headline?: Metric<{ readonly text: string }>;
  onOpen: (label: string) => () => void;
}): React.ReactNode {
  return (
    <div className="panel pad enter">
      <div className="sectitle">
        <h3 className="sec">Estimate vs. evidence-based forecast</h3>
        <span className="cnt">
          the headline finding — declared date next to what actually happens
        </span>
      </div>
      <div className="hero">
        <DateCard
          label="Estimate-derived date"
          basisKind="estimate"
          metric={detail.estimateDerived}
          onOpen={onOpen("Estimate-derived date")}
          render={(v) => (
            <>
              {shortDate(v.date)}
              <div className="meta">
                {v.remainingItemCount} remaining item{v.remainingItemCount === 1 ? "" : "s"} ·{" "}
                {v.estimateUnit === "points" ? "points" : "time"} velocity-ratio projection
              </div>
            </>
          )}
        />
        <DateCard
          label="Evidence-based forecast date"
          basisKind="empirical"
          metric={detail.estimateFreeForecast}
          onOpen={onOpen("Evidence-based forecast date")}
          render={(v) => (
            <>
              {shortDate(v.date)}
              <div className="meta">p50 · {v.modelVersion}</div>
            </>
          )}
        />
      </div>
      {detail.divergence.kind === "value" ? (
        <div className="diverge">
          <span aria-hidden="true">{LATER_SIDE_GLYPH[detail.divergence.value.laterSide]}</span>
          <span>
            Signed gap:{" "}
            <b>
              {decimal(Math.abs(detail.divergence.value.signedDivergenceDays), 1)} day
              {Math.abs(detail.divergence.value.signedDivergenceDays) === 1 ? "" : "s"}
            </b>{" "}
            ({LATER_SIDE_GAP_LABEL[detail.divergence.value.laterSide]})
          </span>
        </div>
      ) : (
        <HonestState
          kind="nodata"
          title="Divergence"
          detail={metricReason(detail.divergence, "N/A")}
        />
      )}
      {headline !== undefined && headline.kind === "value" && (
        <p className="meta">{headline.value.text}</p>
      )}
    </div>
  );
}

function HeadlineComparison({
  headline,
  onOpen,
}: {
  headline: Metric<{ readonly text: string }>;
  onOpen: (label: string) => () => void;
}): React.ReactNode {
  return (
    <div className="panel pad enter">
      <div className="sectitle">
        <h3 className="sec">Estimate vs. evidence-based forecast</h3>
        <span className="cnt">headline finding</span>
      </div>
      {headline.kind === "value" ? (
        <div {...clickable("Estimate vs. forecast", onOpen("Estimate vs. forecast"))}>
          <p className="meta" style={{ fontSize: 16 }}>
            {headline.value.text}
          </p>
          <ConfidencePill value={metricConfidence(headline)} />
        </div>
      ) : (
        <HonestState
          kind="nodata"
          title="Estimate vs. forecast"
          detail={metricReason(headline, "N/A")}
        />
      )}
    </div>
  );
}

function SummaryComparison({
  summary,
  onOpen,
}: {
  summary: EstimationSummaryValue;
  onOpen: (label: string) => () => void;
}): React.ReactNode {
  const laterSide = summary.divergenceLaterSide;
  return (
    <div className="panel pad enter">
      <div className="sectitle">
        <h3 className="sec">Estimate vs. evidence-based forecast</h3>
        <span className="cnt">summary</span>
      </div>
      <div {...clickable("Estimate vs. forecast", onOpen("Estimate vs. forecast"))}>
        <RagDot
          status="neutral"
          label={summary.divergenceObserved ? "Divergence observed" : "No divergence observed"}
        />{" "}
        {summary.divergenceObserved && laterSide !== null ? (
          <span>{CLIENT_LATER_SIDE_TEXT[laterSide]}.</span>
        ) : (
          <span>No estimate-vs-forecast divergence has been recorded for this project yet.</span>
        )}
      </div>
    </div>
  );
}

function ComparisonSection({
  data,
  onOpen,
}: {
  data: EstimationView;
  onOpen: (label: string) => () => void;
}): React.ReactNode {
  if (data.divergenceDetail !== undefined) {
    return (
      <FullComparison
        detail={data.divergenceDetail}
        headline={data.divergenceHeadline}
        onOpen={onOpen}
      />
    );
  }
  if (data.divergenceHeadline !== undefined) {
    return <HeadlineComparison headline={data.divergenceHeadline} onOpen={onOpen} />;
  }
  return <SummaryComparison summary={data.summary} onOpen={onOpen} />;
}

// --- top-level body: AC3's honest-N/A gate, then AC2's front-and-center comparison first, trend +
// gauge below, and the shared click-to-evidence drawer (AC4) -------------------------------------

function EstimationBody({
  projectId,
  data,
}: {
  projectId: string;
  data: EstimationView;
}): React.ReactNode {
  const [openLabel, setOpenLabel] = useState<string | null>(null);
  const onOpen = (label: string) => () => setOpenLabel(label);

  if (data.coverage.kind !== "value") {
    // AC3/TC2: no connector-supplied estimates — the message IS EstimationCapabilityValue's own N/A
    // reason (E13.1), relayed verbatim. Never a chart, a zero, or a client-composed message.
    return <HonestState kind="notobs" detail={`Estimation: ${data.coverage.reason}`} />;
  }

  return (
    <>
      {/* AC2: front and center — placed first, and the only section in the demo's larger `.hero`
          two-column layout, so it reads as the most visually prominent element on the panel. */}
      <ComparisonSection data={data} onOpen={onOpen} />
      <div className="grid cols-2">
        <TrendSection
          accuracy={data.accuracy}
          bias={data.bias}
          spread={data.spread}
          onOpen={onOpen}
        />
        <CommitmentGauge commitmentReliability={data.commitmentReliability} onOpen={onOpen} />
      </div>
      <Drawer
        open={openLabel !== null}
        title={`${openLabel ?? "Estimate"} evidence`}
        subtitle={
          openLabel === null ? undefined : <>Estimate revision history · drill-to-proof trail</>
        }
        onClose={() => setOpenLabel(null)}
      >
        {openLabel !== null && (
          <EvidenceContent
            workItemId={projectId}
            loadingMessage="Loading the estimate evidence trail…"
          />
        )}
      </Drawer>
    </>
  );
}

export function EstimationPanel({ projectId }: { projectId: string }): React.ReactNode {
  const state = useApi<EstimationView>(`/projects/${projectId}/estimation`);
  return (
    <>
      <div className="sectitle">
        <h3 className="sec">Estimation</h3>
        <span className="cnt">
          accuracy · bias · commitment reliability · estimate vs. forecast
        </span>
      </div>
      {state.status === "loading" && <div className="meta">Loading estimation intelligence…</div>}
      {state.status === "error" && (
        <EmptyState
          message={
            state.error.status === 404
              ? "Estimation data is not available for this project."
              : `Couldn't load estimation intelligence: ${state.error.message}`
          }
        />
      )}
      {state.status === "ready" && (
        <EstimationBody projectId={projectId} data={state.envelope.data} />
      )}
    </>
  );
}
